<!-- 
  ============================================
  ESTRUCTURA DE UN COMPONENTE VUE
  ============================================
  
  Un componente Vue tiene 3 secciones principales:
  1. <template> - El HTML que se muestra (la interfaz)
  2. <script> - La lógica JavaScript (datos, métodos, etc.)
  3. <style> - Los estilos CSS (opcional)
-->

<template>
  <!-- 
    v-container: Componente de Vuetify que crea un contenedor con padding
    fluid: Hace que ocupe todo el ancho disponible
  -->
  <v-container fluid>
    <!-- 
      v-row: Crea una fila (como en Bootstrap)
      justify="space-between": Distribuye el espacio entre elementos
    -->
    <v-row justify="space-between" class="px-10">
      <v-col cols="12" sm="12" md="7" lg="6">
        <!-- 
          Botón para volver atrás
          @click: Evento que se ejecuta al hacer click
          $router.go(-1): Método de Vue Router para ir a la página anterior
        -->
        <v-btn
          color="primary"
          icon
          size="20"
          class="text-center"
          @click="$router.go(-1)"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <!-- Título de la página -->
        <p class="text-h6 text--secondary d-inline font-weight-bold">
          Leaderboards Reset Management
        </p>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SECCIÓN 1: RESET POR LEADERBOARD ID (WORKOUT)
      ============================================
    -->
    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Reset by Workout</span>
            <div class="d-flex align-center">
              <!-- 
                Dry Run Switch: Si está activado, simula el reset sin eliminar datos realmente
                Útil para verificar qué se eliminaría antes de hacer el reset real
              -->
              <!-- <span class="mr-2 text-caption">Dry Run</span>
              <v-switch
                v-model="resetById.dryRun"
                color="primary"
                :disabled="isAnyActionActive"
                hide-details
                dense
              ></v-switch> -->
            </div>
          </v-card-title>
          <v-card-subtitle>
            Select a workout type and search for a specific workout to reset its leaderboard
          </v-card-subtitle>
          <v-card-text>
            <v-form ref="formLeaderboardId" v-model="validLeaderboardId">
              <v-row>
                <!-- 
                  Paso 1: Seleccionar el tipo de workout
                -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="resetById.workoutType"
                    :items="workoutTypes"
                    label="Workout Type *"
                    :rules="[v => !!v || 'Workout type is required']"
                    :disabled="isAnyActionActive || loading.workouts"
                    :loading="loading.workouts"
                    outlined
                    dense
                    required
                    @change="onWorkoutTypeChange"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption text--secondary">
                            Select workout type to load workouts
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                    </template>
                  </v-select>
                </v-col>
                
                <!-- 
                  Paso 2: Buscar y seleccionar el workout
                -->
                <v-col cols="12" md="5">
                  <v-autocomplete
                    v-model="resetById.selectedWorkout"
                    :items="availableWorkouts"
                    :search="resetById.workoutSearch"
                    item-text="name"
                    item-value="id"
                    label="Search Workout *"
                    placeholder="Type to search workouts..."
                    :rules="[v => !!v || 'Workout is required']"
                    :disabled="!resetById.workoutType || isAnyActionActive || loading.workouts"
                    :loading="loading.workouts"
                    outlined
                    dense
                    required
                    @change="onWorkoutSelected"
                    clearable
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ resetById.workoutType ? 'No workouts found. Try a different search.' : 'Select a workout type first' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-avatar v-if="item.imageUrl" size="30" class="mr-3">
                        <v-img
                          :src="item.imageUrl"
                          :alt="item.name"
                          cover
                        ></v-img>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                    
                    <!-- Template para mostrar el workout seleccionado con imagen -->
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar v-if="item.imageUrl" size="25" class="mr-2">
                          <v-img
                            :src="item.imageUrl"
                            :alt="item.name"
                            cover
                          ></v-img>
                        </v-avatar>
                        <span>{{ item.name }}</span>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
                
                <!-- 
                  Mostrar el Leaderboard ID encontrado (solo lectura)
                -->
                <!-- <v-col cols="12" md="3" v-if="resetById.leaderboardId">
                  <v-text-field
                    v-model="resetById.leaderboardId"
                    label="Leaderboard ID"
                    outlined
                    dense
                    readonly
                    hint="Auto-detected from selected workout"
                    persistent-hint
                    :rules="[v => !!v || 'Leaderboard ID is required']"
                  ></v-text-field>
                </v-col> -->
                
                <!-- 
                  Mostrar mensaje si no se ha encontrado el leaderboard
                -->
                <v-col cols="12" v-if="resetById.selectedWorkout && !resetById.leaderboardId && !loading.workouts">
                  <v-alert type="warning" dense outlined>
                    Leaderboard not found for the selected workout. Please try selecting a different workout.
                  </v-alert>
                </v-col>
              </v-row>
              
              <v-row>
                <!-- 
                  Botón para ejecutar el reset
                -->
                <v-col cols="12" class="d-flex align-center justify-end">
                  <v-btn
                    color="primary"
                    :loading="loading.resetById"
                    :disabled="!validLeaderboardId || !resetById.leaderboardId || loading.resetById || isAnyActionActive"
                    @click="executeResetById"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Reset Leaderboard Now
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SECCIÓN 2: RESET BY WORKOUT TYPE / ALL LEADERBOARDS
      ============================================
    -->
    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Reset Leaderboards</span>
            <div class="d-flex align-center">
              <!-- 
                Dry Run Switch: Si está activado, simula el reset sin eliminar datos realmente
                Útil para verificar qué se eliminaría antes de hacer el reset real
              -->
              <!-- <span class="mr-2 text-caption">Dry Run</span>
              <v-switch
                v-model="resetByType.dryRun"
                color="primary"
                :disabled="isAnyActionActive"
                hide-details
                dense
              ></v-switch> -->
            </div>
          </v-card-title>
          <v-card-subtitle>
            Reset leaderboards by workout type or all leaderboards
          </v-card-subtitle>
          <v-card-text>
            <!-- 
              v-form: Componente de Vuetify para formularios
              ref="formWorkoutType": Referencia para acceder al formulario desde JavaScript
              v-model="validWorkoutType": Variable que indica si el formulario es válido
            -->
            <v-form ref="formWorkoutType" v-model="validWorkoutType">
              <v-row>
                <!-- 
                  v-select: Dropdown/Select de Vuetify
                  v-model: Vincula el valor seleccionado con una variable del data()
                  :items: Array de opciones para el select (incluye "All Leaderboards")
                  :rules: Reglas de validación
                -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="resetByType.workoutType"
                    :items="workoutTypesWithAll"
                    label="Workout Type *"
                    :rules="[v => v !== null && v !== undefined || 'Workout type is required']"
                    :disabled="isAnyActionActive"
                    outlined
                    dense
                    required
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.value === null" class="text--warning">
                          This will reset ALL leaderboards (use with caution!)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              
              <v-row>
                <!-- Botón para ejecutar el reset -->
                <v-col cols="12" class="d-flex align-center justify-end">
                  <v-btn
                    :color="resetByType.workoutType === null ? 'error' : 'primary'"
                    :loading="loading.resetByType"
                    :disabled="!validWorkoutType || loading.resetByType || isAnyActionActive"
                    @click="executeResetByType"
                  >
                    <v-icon left>{{ resetByType.workoutType === null ? 'mdi-delete-alert' : 'mdi-delete' }}</v-icon>
                    {{ resetByType.workoutType === null ? 'Reset All Leaderboards Now' : 'Reset by Type Now' }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SECCIÓN 2: RESET BY WORKOUT TYPE / ALL LEADERBOARDS
      ============================================
    -->
    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Reset Leaderboards</span>
            <div class="d-flex align-center">
              <!-- 
                Dry Run Switch: Si está activado, simula el reset sin eliminar datos realmente
                Útil para verificar qué se eliminaría antes de hacer el reset real
              -->
              <!-- <span class="mr-2 text-caption">Dry Run</span>
              <v-switch
                v-model="resetByType.dryRun"
                color="primary"
                :disabled="isAnyActionActive"
                hide-details
                dense
              ></v-switch> -->
            </div>
          </v-card-title>
          <v-card-subtitle>
            Reset leaderboards by workout type or all leaderboards
          </v-card-subtitle>
          <v-card-text>
            <!-- 
              v-form: Componente de Vuetify para formularios
              ref="formWorkoutType": Referencia para acceder al formulario desde JavaScript
              v-model="validWorkoutType": Variable que indica si el formulario es válido
            -->
            <v-form ref="formWorkoutType" v-model="validWorkoutType">
              <v-row>
                <!-- 
                  v-select: Dropdown/Select de Vuetify
                  v-model: Vincula el valor seleccionado con una variable del data()
                  :items: Array de opciones para el select (incluye "All Leaderboards")
                  :rules: Reglas de validación
                -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="resetByType.workoutType"
                    :items="workoutTypesWithAll"
                    label="Workout Type *"
                    :rules="[v => v !== null && v !== undefined || 'Workout type is required']"
                    :disabled="isAnyActionActive"
                    outlined
                    dense
                    required
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.value === null" class="text--warning">
                          This will reset ALL leaderboards (use with caution!)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              
              <v-row>
                <!-- Botón para ejecutar el reset -->
                <v-col cols="12" class="d-flex align-center justify-end">
                  <v-btn
                    :color="resetByType.workoutType === null ? 'error' : 'primary'"
                    :loading="loading.resetByType"
                    :disabled="!validWorkoutType || loading.resetByType || isAnyActionActive"
                    @click="executeResetByType"
                  >
                    <v-icon left>{{ resetByType.workoutType === null ? 'mdi-delete-alert' : 'mdi-delete' }}</v-icon>
                    {{ resetByType.workoutType === null ? 'Reset All Leaderboards Now' : 'Reset by Type Now' }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SECCIÓN 3: PROGRAMAR RESET (SCHEDULER)
      ============================================
    -->
    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Schedule Reset</span>
            <div class="d-flex align-center">
              <!-- 
                Dry Run Switch: Si está activado, el job programado ejecutará un dry run
                Cuando el job se ejecute, simulará el reset sin eliminar datos realmente
              -->
              <!-- <span class="mr-2 text-caption">Dry Run</span>
              <v-switch
                v-model="scheduleConfig.dryRun"
                color="primary"
                :disabled="isAnyActionActive"
                hide-details
                dense
              ></v-switch> -->
            </div>
          </v-card-title>
          <v-card-subtitle>
            Schedule a reset for all leaderboards at a specific date and time
          </v-card-subtitle>
          <v-card-text>
            <v-form ref="formSchedule" v-model="validSchedule">
              <v-row>
                <!-- 
                  v-text-field con type="datetime-local": Campo para fecha y hora
                -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="scheduleConfig.schedule"
                    label="Date & Time *"
                    type="datetime-local"
                    :min="minDateTime"
                    :max="maxDateTime"
                    hint="Must be within 30 days (Cloud Scheduler limit)"
                    persistent-hint
                    :rules="[
                      v => !!v || 'Date and time is required',
                      v => {
                        if (!v) return true; // Already handled by required rule
                        const selectedDate = new Date(v);
                        const now = new Date();
                        if (selectedDate <= now) {
                          return 'Date and time must be in the future';
                        }
                        const diffDays = (selectedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
                        if (diffDays > 30) {
                          return 'Date cannot be more than 30 days in the future (Cloud Scheduler limit)';
                        }
                        return true;
                      }
                    ]"
                    :disabled="isAnyActionActive"
                    outlined
                    dense
                    required
                    @input="updateTimePreview"
                  ></v-text-field>
                </v-col>
                
                <!-- 
                  Selector de zona horaria
                -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="scheduleConfig.timeZone"
                    :items="timeZones"
                    label="Time Zone *"
                    :rules="[v => !!v || 'Time zone is required']"
                    :disabled="isAnyActionActive"
                    outlined
                    dense
                    required
                    @change="updateTimePreview"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
                
                <!-- 
                  Información sobre qué se va a resetear
                  Alineado usando padding-top que coincide con la altura del label de los campos dense
                -->
                <v-col cols="12" md="4">
                  <v-alert 
                    type="info" 
                    dense 
                    outlined 
                    class="mb-0"
                  >
                    This will reset <strong>all leaderboards</strong> at the scheduled time
                  </v-alert>
                </v-col>
                
                <!-- 
                  Vista previa de la hora en diferentes zonas horarias
                -->
                <v-col cols="12" v-if="scheduleConfig.schedule && scheduleConfig.timeZone">
                  <v-alert type="info" dense outlined>
                    <div class="text-body-2 font-weight-bold mb-2">Scheduled Time Preview:</div>
                    <div v-for="(preview, index) in timePreview" :key="index" class="text-caption">
                      <strong>{{ preview.label }}:</strong> {{ preview.time }}
                    </div>
                  </v-alert>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" class="d-flex align-center justify-end">
                  <v-btn
                    color="primary"
                    :loading="loading.schedule"
                    :disabled="!validSchedule || loading.schedule || isAnyActionActive"
                    @click="createSchedule"
                  >
                    <v-icon left>mdi-calendar-clock</v-icon>
                    Schedule Reset
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SECCIÓN 5: LISTA DE JOBS PROGRAMADOS
      ============================================
    -->
    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Scheduled Jobs
            <v-spacer></v-spacer>
            <!-- 
              v-btn con @click: Botón que ejecuta un método al hacer click
            -->
            <v-btn
              icon
              small
              @click="loadScheduledJobs"
              :loading="loading.jobs"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- 
              v-data-table: Tabla de datos de Vuetify
              :items: Array de datos a mostrar
              :headers: Definición de las columnas
              :loading: Estado de carga
            -->
            <v-data-table
              :headers="jobHeaders"
              :items="scheduledJobs"
              :loading="loading.jobs"
              class="elevation-1"
            >
              <!-- 
                Template para personalizar cómo se muestra una columna
                v-slot:item.state: Slot para la columna "state"
              -->
              <template v-slot:item.state="{ item }">
                <v-chip
                  :color="getStateColor(item.state)"
                  small
                  dark
                >
                  {{ item.state }}
                </v-chip>
              </template>
              
              <!-- Template para Last Execution -->
              <template v-slot:item.lastAttemptTime="{ item }">
                <span v-if="item.lastAttemptTime">
                  {{ formatTimestamp(item.lastAttemptTime) }}
                </span>
                <span v-else class="text--disabled">
                  Not executed yet
                </span>
              </template>
              
              <!-- Template para Next Run -->
              <template v-slot:item.nextRunTime="{ item }">
                <span v-if="item.nextRunTime">
                  {{ formatTimestamp(item.nextRunTime) }}
                </span>
                <span v-else class="text--disabled">
                  N/A
                </span>
              </template>
              
              <!-- Template para acciones (botones) -->
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="deleteJob(item.jobId)"
                  title="Cancel scheduled job"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 
      ============================================
      SNACKBAR: Mensaje de notificación
      ============================================
      v-snackbar: Componente de Vuetify para mostrar mensajes
      v-model: Controla si se muestra o no
      :color: Color del mensaje (success, error, warning, info)
    -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- 
      ============================================
      DIALOG DE CONFIRMACIÓN PERSONALIZADO
      ============================================
    -->
    <v-dialog v-model="dialogConfirm.show" width="400" class="rounded-xl" persistent>
      <v-card class="text-center">
        <v-icon class="text-center ma-2" size="120" color="primary">mdi-alert</v-icon>
        <v-card-text class="pa-4 text-center">
          {{ dialogConfirm.message }}
        </v-card-text>
        <v-card-text class="text-center">Are you sure?</v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn
            color="third"
            tile
            width="150"
            class="pa-0 text--secondary mr-2"
            @click="closeDialogConfirm()"
          >
            No
          </v-btn>
          <v-btn
            color="primary"
            tile
            width="150"
            class="pa-0"
            @click="confirmAction()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
/* 
  ============================================
  SECCIÓN SCRIPT - LÓGICA DEL COMPONENTE
  ============================================
  
  Aquí se define:
  - data(): Variables reactivas del componente
  - methods: Funciones/métodos que se pueden llamar
  - computed: Propiedades calculadas (se actualizan automáticamente)
  - lifecycle hooks: created(), mounted(), etc.
*/

// Importar Vue para usar Vue.axios
import Vue from "vue";
// Importar requestEndPoint que tiene las URLs de las funciones
import { requestEndPoint } from "@/store/Api";
// Importar funciones de Firestore para cargar workouts
import { getDb } from "@/firebase";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";

export default {
  // Nombre del componente (útil para debugging)
  name: "LeaderboardsReset",
  
  // 
  // data(): Función que retorna un objeto con todas las variables reactivas
  // Cuando cambias estas variables, Vue actualiza automáticamente la vista
  //
  data: () => ({
    // Variables para validar formularios
    validWorkoutType: false,
    validLeaderboardId: false,
    validSchedule: false,
    
    // Datos del formulario "Reset by Type / All"
    // Ahora incluye la opción "All" (null) para resetear todos los leaderboards
    resetByType: {
      workoutType: null, // null = todos, o "coachedClass", "live", "preSet"
      dryRun: false, // Por defecto en modo real (no prueba)
    },
    
    // Datos del formulario "Reset by ID"
    resetById: {
      workoutType: null, // Tipo de workout seleccionado
      selectedWorkout: null, // Workout seleccionado (objeto con id y name)
      workoutSearch: "", // Texto de búsqueda para el autocomplete
      leaderboardId: "", // ID del leaderboard (se obtiene automáticamente)
      dryRun: false, // Por defecto en modo real (no prueba)
    },
    
    // Lista de workouts disponibles según el tipo seleccionado
    availableWorkouts: [],
    
    // Datos del formulario "Schedule"
    // Nota: workoutType siempre será null porque solo se puede programar para todos los leaderboards
    scheduleConfig: {
      schedule: "", // Fecha y hora en formato datetime-local (sin zona horaria)
      timeZone: "America/Mexico_City", // Zona horaria por defecto (México)
      dryRun: false, // Por defecto en modo real (no prueba)
    },
    
    // Zonas horarias disponibles
    timeZones: [
      { text: "Hermosillo, Mexico (MST)", value: "America/Hermosillo", subtitle: "UTC-7" },
      { text: "Mexico City (CST/CDT)", value: "America/Mexico_City", subtitle: "UTC-6 / UTC-5" },
      { text: "Detroit/Michigan (EST/EDT)", value: "America/Detroit", subtitle: "UTC-5 / UTC-4" },
      { text: "UTC (Coordinated Universal Time)", value: "UTC", subtitle: "UTC+0" },
      { text: "New York (EST/EDT)", value: "America/New_York", subtitle: "UTC-5 / UTC-4" },
      { text: "Los Angeles (PST/PDT)", value: "America/Los_Angeles", subtitle: "UTC-8 / UTC-7" },
    ],
    
    // Vista previa de la hora en diferentes zonas horarias
    timePreview: [],
    
    // Fecha/hora mínima para el selector (ahora)
    minDateTime: "",
    
    // Fecha/hora máxima para el selector (30 días desde ahora - límite de Cloud Scheduler)
    maxDateTime: "",
    
    // Opciones para los selects
    workoutTypes: [
      { text: "Coached Classes", value: "coachedClass" },
      { text: "Lives", value: "live" },
      { text: "PreSets", value: "preSet" },
    ],
    
    workoutTypesWithAll: [
      { text: "All Leaderboards", value: null },
      { text: "Coached Classes", value: "coachedClass" },
      { text: "Lives", value: "live" },
      { text: "PreSets", value: "preSet" },
    ],
    
    // Estados de carga (loading) para cada acción
    // Esto muestra un spinner mientras se ejecuta la función
    loading: {
      resetByType: false,
      resetById: false,
      schedule: false,
      jobs: false,
      workouts: false, // Para cargar la lista de workouts
    },
    
    // Variable para rastrear qué acción está activa
    // Cuando una acción está en progreso, las demás se deshabilitan
    activeAction: null, // Puede ser: 'resetByType', 'resetById', 'schedule', null
    
    // Lista de jobs programados
    scheduledJobs: [],
    
    // Headers para la tabla de jobs
    jobHeaders: [
      { text: "Job ID", value: "jobId" },
      { text: "Description", value: "description" },
      { text: "Schedule", value: "schedule" },
      { text: "State", value: "state" },
      { text: "Last Execution", value: "lastAttemptTime" },
      { text: "Next Run", value: "nextRunTime" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    
    // Snackbar: Para mostrar mensajes de éxito/error
    snackbar: {
      show: false,
      text: "",
      color: "success",
      timeout: 5000,
    },
    
    // Dialog de confirmación personalizado
    dialogConfirm: {
      show: false,
      message: "",
      action: null, // Guardará la función a ejecutar después de confirmar
      jobId: null, // Para guardar temporalmente el jobId cuando se cancela un job
    },
  }),
  
  // 
  // created(): Se ejecuta cuando el componente se crea
  // Es un "lifecycle hook" de Vue
  //
  created() {
    // Cargar los jobs programados al entrar a la página
    this.loadScheduledJobs();
    
    // Establecer la fecha/hora mínima como "ahora" en formato datetime-local
    this.updateMinDateTime();
  },
  
  // 
  // watch: Observa cambios en variables y ejecuta funciones cuando cambian
  //
  watch: {
    // Observar cambios en schedule y timeZone para actualizar la vista previa
    'scheduleConfig.schedule'() {
      this.updateTimePreview();
    },
    'scheduleConfig.timeZone'() {
      this.updateTimePreview();
    },
  },
  
  // 
  // computed: Propiedades calculadas que se actualizan automáticamente
  // Son como variables pero se recalculan cuando cambian las dependencias
  //
  computed: {
    // 
    // isAnyActionActive: Verifica si alguna acción está en progreso
    // Retorna true si cualquier loading está activo
    //
    isAnyActionActive() {
      return (
        this.loading.resetByType ||
        this.loading.resetById ||
        this.loading.schedule
      );
    },
  },
  
  // 
  // methods: Todas las funciones que puedes llamar desde el template
  // Se acceden con @click="nombreFuncion" o this.nombreFuncion()
  //
  methods: {
    /* 
      ============================================
      MÉTODO 1: Ejecutar Reset por Workout Type
      ============================================
    */
    async executeResetByType() {
      // Validar el formulario
      if (!this.$refs.formWorkoutType.validate()) {
        return;
      }
      
      // Preparar mensaje de confirmación
      let confirmMessage;
      if (this.resetByType.workoutType === null) {
        confirmMessage = "Are you sure you want to reset ALL leaderboards? This action cannot be undone.";
      } else {
        const workoutTypeLabel = this.workoutTypes.find(t => t.value === this.resetByType.workoutType)?.text || this.resetByType.workoutType;
        confirmMessage = `Are you sure you want to reset all leaderboards for "${workoutTypeLabel}"? This action cannot be undone.`;
      }
      
      // Abrir diálogo de confirmación
      this.openDialogConfirm(confirmMessage, 'executeResetByTypeConfirmed');
    },
    
    async executeResetByTypeConfirmed() {
      // Marcar esta acción como activa
      this.activeAction = 'resetByType';
      
      // Activar loading (muestra spinner en el botón)
      this.loading.resetByType = true;
      
      try {
        // Si workoutType es null, usar resetAllLeaderboards
        // Si workoutType tiene valor, usar resetByWorkoutType
        let endpoint;
        let payload;
        
        if (this.resetByType.workoutType === null) {
          // Resetear todos los leaderboards
          endpoint = requestEndPoint.resetAllLeaderboards;
          payload = {
            dryRun: this.resetByType.dryRun,
          };
        } else {
          // Resetear por tipo específico
          endpoint = requestEndPoint.resetByWorkoutType;
          payload = {
            workoutType: this.resetByType.workoutType,
            dryRun: this.resetByType.dryRun,
          };
        }
        
        const response = await Vue.axios.post(endpoint, payload);
        
        // Si llegamos aquí, la función se ejecutó correctamente
        const actionText = this.resetByType.workoutType === null ? 'all leaderboards' : `workout type: ${this.resetByType.workoutType}`;
        const leaderboardsCount = response.data.leaderboardsProcessed || 0;
        this.showSnackbar(
          `Success! ${response.data.deletedCount} documents ${this.resetByType.dryRun ? 'would be deleted' : 'deleted'} from ${leaderboardsCount} leaderboards (${actionText})`,
          "success"
        );
        
        // Si no es dry run, recargar los jobs
        if (!this.resetByType.dryRun) {
          this.loadScheduledJobs();
        }
        
      } catch (error) {
        // Si hay un error, mostrarlo
        console.error("Error resetting:", error);
        this.showSnackbar(
          error.response?.data?.error || "Error executing reset",
          "error"
        );
      } finally {
        // Siempre desactivar loading al terminar
        this.loading.resetByType = false;
        this.activeAction = null; // Liberar la acción
      }
    },
    
    /* 
      ============================================
      MÉTODO 2A: Cargar Workouts cuando cambia el tipo
      ============================================
    */
    async onWorkoutTypeChange() {
      // Limpiar selección anterior
      this.resetById.selectedWorkout = null;
      this.resetById.leaderboardId = "";
      this.resetById.workoutSearch = "";
      this.availableWorkouts = [];
      
      if (!this.resetById.workoutType) {
        return;
      }
      
      this.loading.workouts = true;
      
      try {
        const db = getDb();
        if (!db) {
          throw new Error("Firebase Firestore not initialized");
        }
        
        // Determinar qué colección usar según el tipo
        let collectionName;
        switch (this.resetById.workoutType) {
          case 'coachedClass':
            collectionName = 'proTrainersList';
            break;
          case 'live':
            collectionName = 'livesList';
            break;
          case 'preSet':
            collectionName = 'preSetsList';
            break;
          default:
            throw new Error(`Unknown workout type: ${this.resetById.workoutType}`);
        }
        
        // Cargar todos los workouts de esa colección
        const workoutsRef = collection(db, collectionName);
        const workoutsSnapshot = await getDocs(workoutsRef);
        
        // Mapear los workouts a un formato simple con id, name e imagen
        this.availableWorkouts = workoutsSnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Obtener la URL de la imagen según el tipo de workout
          let imageUrl = null;
          let workoutName = null;
          
          switch (this.resetById.workoutType) {
            case 'coachedClass':
              // Para coachedClass, la imagen está en selectedImagePreview.link
              imageUrl = data.selectedImagePreview?.link || null;
              workoutName = data.name || `Workout ${doc.id}`;
              break;
            case 'live':
              // Para live, la imagen está en image.link y el nombre puede estar en title.ENG o title
              imageUrl = data.image?.link || null;
              workoutName = data.title?.ENG || data.title || `Live ${doc.id}`;
              break;
            case 'preSet':
              // Para preSet, la imagen está en image.link o image, y el nombre puede estar en name.ENG o name
              imageUrl = data.image?.link || data.image || null;
              workoutName = data.name?.ENG || data.name || `PreSet ${doc.id}`;
              break;
            default:
              workoutName = data.name || `Workout ${doc.id}`;
          }
          
          return {
            id: doc.id,
            name: workoutName,
            imageUrl: imageUrl,
          };
        }).sort((a, b) => a.name.localeCompare(b.name)); // Ordenar alfabéticamente
        
        console.log(`[onWorkoutTypeChange] Loaded ${this.availableWorkouts.length} workouts from ${collectionName}`);
        
      } catch (error) {
        console.error("Error loading workouts:", error);
        this.showSnackbar(
          `Error loading workouts: ${error.message}`,
          "error"
        );
        this.availableWorkouts = [];
      } finally {
        this.loading.workouts = false;
      }
    },
    
    /* 
      ============================================
      MÉTODO 2B: Buscar Leaderboard cuando se selecciona un workout
      ============================================
    */
    async onWorkoutSelected() {
      if (!this.resetById.selectedWorkout || !this.resetById.workoutType) {
        this.resetById.leaderboardId = "";
        return;
      }
      
      this.loading.workouts = true;
      
      try {
        const db = getDb();
        if (!db) {
          throw new Error("Firebase Firestore not initialized");
        }
        
        const workoutId = this.resetById.selectedWorkout;
        
        // Buscar el leaderboard que tenga este workout como referencia
        // Los leaderboards tienen un campo 'workout' que es una referencia al workout
        const leaderboardsRef = collection(db, 'leaderboards');
        
        // Determinar la ruta de la referencia del workout
        let workoutRefPath;
        switch (this.resetById.workoutType) {
          case 'coachedClass':
            workoutRefPath = `proTrainersList/${workoutId}`;
            break;
          case 'live':
            workoutRefPath = `livesList/${workoutId}`;
            break;
          case 'preSet':
            workoutRefPath = `preSetsList/${workoutId}`;
            break;
          default:
            throw new Error(`Unknown workout type: ${this.resetById.workoutType}`);
        }
        
        // Buscar el leaderboard por la referencia del workout
        // Nota: Firestore no permite hacer queries directas por referencia en el cliente
        // Necesitamos buscar por workoutName o hacer una query diferente
        // Alternativa: buscar todos los leaderboards y filtrar, o usar el ID del workout directamente
        
        // Opción 1: El leaderboard ID es el mismo que el workout ID (según el código que vi)
        // Verificamos si existe un leaderboard con ese ID
        const leaderboardRef = doc(db, 'leaderboards', workoutId);
        const leaderboardDoc = await getDoc(leaderboardRef);
        
        if (leaderboardDoc.exists()) {
          this.resetById.leaderboardId = workoutId;
          console.log(`[onWorkoutSelected] Found leaderboard with ID: ${workoutId}`);
        } else {
          // Si no existe con ese ID, buscar por workoutName
          const selectedWorkout = this.availableWorkouts.find(w => w.id === workoutId);
          if (selectedWorkout) {
            // Buscar leaderboards que tengan ese nombre
            const q = query(leaderboardsRef, where('workoutName', '==', selectedWorkout.name));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              // Tomar el primer resultado
              this.resetById.leaderboardId = querySnapshot.docs[0].id;
              console.log(`[onWorkoutSelected] Found leaderboard by name with ID: ${this.resetById.leaderboardId}`);
            } else {
              throw new Error(`No leaderboard found for workout: ${selectedWorkout.name}`);
            }
          } else {
            throw new Error(`Workout not found in available workouts`);
          }
        }
        
      } catch (error) {
        console.error("Error finding leaderboard:", error);
        this.showSnackbar(
          `Error finding leaderboard: ${error.message}`,
          "error"
        );
        this.resetById.leaderboardId = "";
      } finally {
        this.loading.workouts = false;
      }
    },
    
    /* 
      ============================================
      MÉTODO 2C: Ejecutar Reset por Leaderboard ID
      ============================================
    */
    async executeResetById() {
      if (!this.$refs.formLeaderboardId.validate()) {
        return;
      }
      
      if (!this.resetById.leaderboardId) {
        this.showSnackbar(
          "Please select a workout first to find its leaderboard",
          "error"
        );
        return;
      }
      
      // Preparar mensaje de confirmación
      const selectedWorkout = this.availableWorkouts.find(w => w.id === this.resetById.selectedWorkout);
      const workoutName = selectedWorkout ? selectedWorkout.name : 'the selected workout';
      const confirmMessage = `Are you sure you want to reset the leaderboard for "${workoutName}"? This action cannot be undone.`;
      
      // Abrir diálogo de confirmación
      this.openDialogConfirm(confirmMessage, 'executeResetByIdConfirmed');
    },
    
    async executeResetByIdConfirmed() {
      this.activeAction = 'resetById';
      this.loading.resetById = true;
      
      try {
        // Enviar solo los datos necesarios para el reset
        const payload = {
          leaderboardId: this.resetById.leaderboardId,
          dryRun: this.resetById.dryRun,
        };
        
        const response = await Vue.axios.post(
          requestEndPoint.resetByLeaderboardId,
          payload
        );
        
        this.showSnackbar(
          `Success! ${response.data.deletedCount} documents ${this.resetById.dryRun ? 'would be deleted' : 'deleted'}`,
          "success"
        );
        
      } catch (error) {
        console.error("Error resetting by ID:", error);
        this.showSnackbar(
          error.response?.data?.error || "Error executing reset",
          "error"
        );
      } finally {
        this.loading.resetById = false;
        this.activeAction = null;
      }
    },
    
    /* 
      ============================================
      MÉTODO 3: Ejecutar Reset de Todos
      ============================================
    */
    async executeResetAll() {
      // Abrir diálogo de confirmación
      const confirmMessage = "Are you sure you want to reset ALL leaderboards? This action cannot be undone.";
      this.openDialogConfirm(confirmMessage, 'executeResetAllConfirmed');
    },
    
    async executeResetAllConfirmed() {
      this.activeAction = 'resetAll';
      this.loading.resetAll = true;
      
      try {
        // Preparar los datos - siempre resetear todos (workoutType es null)
        const payload = {
          dryRun: this.resetAll.dryRun,
          // workoutType siempre es null porque solo se puede resetear todos los leaderboards
        };
        
        const response = await Vue.axios.post(
          requestEndPoint.resetAllLeaderboards,
          payload
        );
        
        this.showSnackbar(
          `Success! ${response.data.deletedCount} documents ${this.resetAll.dryRun ? 'would be deleted' : 'deleted'} from ${response.data.leaderboardsProcessed} leaderboards`,
          "success"
        );
        
      } catch (error) {
        console.error("Error resetting all:", error);
        this.showSnackbar(
          error.response?.data?.error || "Error executing reset",
          "error"
        );
      } finally {
        this.loading.resetAll = false;
        this.activeAction = null;
      }
    },
    
    /* 
      ============================================
      MÉTODO 4: Crear un Job Programado
      ============================================
    */
    /* 
      ============================================
      MÉTODO 3A: Actualizar Vista Previa de Hora
      ============================================
    */
    updateTimePreview() {
      if (!this.scheduleConfig.schedule || !this.scheduleConfig.timeZone) {
        this.timePreview = [];
        return;
      }
      
      try {
        // datetime-local da una fecha en formato "YYYY-MM-DDTHH:mm" sin zona horaria
        // Necesitamos interpretarla como si fuera en la zona horaria seleccionada
        const localDateString = this.scheduleConfig.schedule;
        const selectedTimeZone = this.scheduleConfig.timeZone;
        
        // Parsear la fecha/hora
        const [datePart, timePart] = localDateString.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        // Crear una fecha que represente ese momento en la zona horaria seleccionada
        // Usamos una técnica: crear la fecha en UTC y luego ajustar según el offset de la zona
        const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        
        // Crear una fecha temporal (será interpretada como hora local del navegador)
        const tempDate = new Date(dateString);
        
        // Obtener el offset de la zona horaria seleccionada
        // Creamos una fecha en UTC y vemos cómo se representa en la zona seleccionada
        const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
        const formatterSelected = new Intl.DateTimeFormat('en-US', {
          timeZone: selectedTimeZone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        
        // Calcular el offset comparando UTC con la zona seleccionada
        const utcString = utcDate.toISOString();
        const selectedString = new Date(utcString).toLocaleString('en-US', { timeZone: selectedTimeZone });
        
        // Crear la fecha correcta: interpretar la hora ingresada como si fuera en selectedTimeZone
        // Para esto, necesitamos crear una fecha UTC que, cuando se muestre en selectedTimeZone, dé la hora ingresada
        const targetDate = this.createDateInTimeZone(year, month - 1, day, hours, minutes, selectedTimeZone);
        
        // Crear formateadores para diferentes zonas horarias
        const zonesToShow = [
          { label: "Selected Time Zone (" + this.getTimeZoneLabel(selectedTimeZone) + ")", value: selectedTimeZone },
          { label: "Mexico City", value: "America/Mexico_City" },
          { label: "Michigan/Detroit", value: "America/Detroit" },
          { label: "UTC", value: "UTC" },
        ];
        
        // Para cada zona horaria, mostrar cómo se verá esa fecha/hora
        this.timePreview = zonesToShow.map(zone => {
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone.value,
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
          });
          
          return {
            label: zone.label,
            time: formatter.format(targetDate),
          };
        });
        
      } catch (error) {
        console.error("Error updating time preview:", error);
        this.timePreview = [];
      }
    },
    
    /* 
      Helper: Crear una fecha que represente una hora específica en una zona horaria
    */
    createDateInTimeZone(year, month, day, hours, minutes, timeZone) {
      // Convertir la fecha/hora local (en la zona horaria especificada) a UTC
      // Estrategia simple: usar Intl para crear la fecha correctamente
      
      // Crear un string de fecha en formato ISO (month ya viene como 0-indexed)
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
      
      // Crear una fecha como si fuera UTC (punto de partida)
      let utcDate = new Date(dateString + 'Z');
      
      // Obtener cómo se ve esa fecha UTC en la zona horaria objetivo
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      
      // Iterar hasta encontrar la fecha UTC correcta
      // (máximo 3 intentos, normalmente se resuelve en 1-2)
      for (let i = 0; i < 3; i++) {
        const parts = formatter.formatToParts(utcDate);
        const tzYear = parseInt(parts.find(p => p.type === 'year').value);
        const tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
        const tzDay = parseInt(parts.find(p => p.type === 'day').value);
        const tzHour = parseInt(parts.find(p => p.type === 'hour').value);
        const tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
        
        // Si coincide, retornar
        if (tzYear === year && tzMonth === month && tzDay === day && tzHour === hours && tzMinute === minutes) {
          return utcDate;
        }
        
        // Calcular diferencia y ajustar
        const tzTime = new Date(Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, 0)).getTime();
        const desiredTime = new Date(Date.UTC(year, month, day, hours, minutes, 0)).getTime();
        const diff = desiredTime - tzTime;
        utcDate = new Date(utcDate.getTime() + diff);
      }
      
      // Si después de 3 intentos no coincide exactamente, retornar la mejor aproximación
      return utcDate;
    },
    
    /* 
      Helper: Obtener etiqueta amigable para la zona horaria
    */
    getTimeZoneLabel(timeZone) {
      const tz = this.timeZones.find(tz => tz.value === timeZone);
      return tz ? tz.text : timeZone;
    },
    
    /*
      ============================================
      MÉTODOS PARA EL DIALOG DE CONFIRMACIÓN
      ============================================
    */
    openDialogConfirm(message, action) {
      this.dialogConfirm.message = message;
      this.dialogConfirm.action = action;
      this.dialogConfirm.show = true;
    },
    
    closeDialogConfirm() {
      this.dialogConfirm.show = false;
      this.dialogConfirm.message = "";
      this.dialogConfirm.action = null;
      this.dialogConfirm.jobId = null;
    },
    
    confirmAction() {
      // Guardar el action y jobId antes de cerrar el diálogo
      const action = this.dialogConfirm.action;
      const jobId = this.dialogConfirm.jobId; // Guardar jobId antes de limpiarlo
      
      // Cerrar el diálogo (esto limpiará jobId)
      this.closeDialogConfirm();
      
      // Restaurar jobId si existe (para acciones que lo necesiten)
      if (jobId) {
        this.dialogConfirm.jobId = jobId;
      }
      
      // Ejecutar la acción confirmada
      if (action && typeof this[action] === 'function') {
        this[action]();
      }
    },
    
    /*
      ============================================
      MÉTODOS DE VALIDACIÓN
      ============================================
    */
    // Actualizar la fecha/hora mínima (ahora) y máxima (30 días desde ahora)
    updateMinDateTime() {
      const now = new Date();
      // Formato datetime-local: YYYY-MM-DDTHH:mm
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      this.minDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      // Calcular fecha máxima (30 días desde ahora - límite de Cloud Scheduler)
      const maxDate = new Date(now);
      maxDate.setDate(maxDate.getDate() + 30);
      const maxYear = maxDate.getFullYear();
      const maxMonth = String(maxDate.getMonth() + 1).padStart(2, '0');
      const maxDay = String(maxDate.getDate()).padStart(2, '0');
      const maxHours = String(maxDate.getHours()).padStart(2, '0');
      const maxMinutes = String(maxDate.getMinutes()).padStart(2, '0');
      this.maxDateTime = `${maxYear}-${maxMonth}-${maxDay}T${maxHours}:${maxMinutes}`;
    },
    
    // Validar que la fecha/hora sea en el futuro y dentro del límite de 30 días
    validateFutureDateTime(dateTimeString) {
      if (!dateTimeString) {
        return false;
      }
      
      // Convertir la fecha seleccionada a Date object
      const selectedDate = new Date(dateTimeString);
      const now = new Date();
      
      // La fecha seleccionada debe ser mayor que ahora
      if (selectedDate <= now) {
        return false;
      }
      
      // Cloud Scheduler tiene un límite de 30 días para jobs programados
      // Calcular la diferencia en días
      const diffTime = selectedDate.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      
      // El límite es 30 días (Cloud Scheduler restriction)
      const maxDays = 30;
      
      if (diffDays > maxDays) {
        return false;
      }
      
      return true;
    },
    
    /* 
      ============================================
      MÉTODO 4: Crear un Job Programado
      ============================================
    */
    async createSchedule() {
      console.log("[createSchedule] Method called");
      console.log("[createSchedule] Form valid:", this.$refs.formSchedule?.validate());
      console.log("[createSchedule] scheduleConfig:", this.scheduleConfig);
      
      if (!this.$refs.formSchedule.validate()) {
        console.log("[createSchedule] Form validation failed");
        return;
      }
      
      // Validar que la fecha/hora sea en el futuro
      if (!this.validateFutureDateTime(this.scheduleConfig.schedule)) {
        console.log("[createSchedule] Date validation failed");
        this.showSnackbar(
          "Please select a date and time in the future",
          "error"
        );
        return;
      }
      
      if (!this.scheduleConfig.timeZone) {
        console.log("[createSchedule] TimeZone validation failed");
        this.showSnackbar(
          "Please select a time zone",
          "error"
        );
        return;
      }
      
      console.log("[createSchedule] All validations passed");
      this.activeAction = 'schedule';
      this.loading.schedule = true;
      
      try {
        // Convertir la fecha del input a formato ISO en la zona horaria seleccionada
        // datetime-local da una fecha sin zona horaria, necesitamos interpretarla en la zona seleccionada
        const localDateString = this.scheduleConfig.schedule;
        const timeZone = this.scheduleConfig.timeZone;
        
        console.log("[createSchedule] localDateString:", localDateString);
        console.log("[createSchedule] timeZone:", timeZone);
        
        // Parsear la fecha/hora
        const [datePart, timePart] = localDateString.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        console.log("[createSchedule] Parsed date:", { year, month, day, hours, minutes });
        
        // Crear una fecha que represente ese momento en la zona horaria seleccionada
        // Usamos la función helper para crear la fecha correcta
        const targetDate = this.createDateInTimeZone(year, month - 1, day, hours, minutes, timeZone);
        
        console.log("[createSchedule] targetDate:", targetDate);
        
        // Convertir a ISO string (UTC) - Cloud Scheduler espera ISO string
        const scheduleDate = targetDate.toISOString();
        
        console.log("[createSchedule] scheduleDate (ISO):", scheduleDate);
        
        // Preparar la configuración del job
        const jobConfig = {
          schedule: scheduleDate,
          timeZone: timeZone, // IMPORTANTE: Especificar la zona horaria
          // workoutType siempre es null porque solo se puede programar para todos los leaderboards
          workoutType: null,
          description: `Reset all leaderboards on ${new Date(scheduleDate).toLocaleString('en-US', { timeZone: timeZone })} (${timeZone})${this.scheduleConfig.dryRun ? ' [DRY RUN]' : ''}`,
          dryRun: this.scheduleConfig.dryRun,
        };
        console.log("[createSchedule] jobConfig:", jobConfig);
        const response = await Vue.axios.post(
          requestEndPoint.scheduleResetLeaderboards,
          {
            action: "create",
            jobConfig: jobConfig,
          }
        );
        
        // Mostrar mensaje de éxito con la hora en la zona horaria seleccionada
        const successMessage = `Job scheduled successfully! It will run on ${new Date(scheduleDate).toLocaleString('en-US', { 
          timeZone: timeZone,
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short'
        })} (${timeZone})`;
        
        this.showSnackbar(successMessage, "success");
        
        // Limpiar el formulario
        this.scheduleConfig.schedule = "";
        this.scheduleConfig.timeZone = "America/Mexico_City"; // Resetear a la zona por defecto
        this.timePreview = [];
        
        // Recargar la lista de jobs
        this.loadScheduledJobs();
        
      } catch (error) {
        console.error("Error creating schedule:", error);
        console.error("Error details:", error.response?.data);
        
        // Mostrar mensaje de error más detallado
        const errorMessage = error.response?.data?.message 
          ? `${error.response.data.error}: ${error.response.data.message}`
          : error.response?.data?.error || "Error creating schedule";
        
        this.showSnackbar(
          errorMessage,
          "error"
        );
      } finally {
        this.loading.schedule = false;
        this.activeAction = null;
      }
    },
    
    /* 
      ============================================
      MÉTODO 5: Cargar Lista de Jobs Programados
      ============================================
    */
    async loadScheduledJobs() {
      this.loading.jobs = true;
      
      try {
        const response = await Vue.axios.post(
          requestEndPoint.scheduleResetLeaderboards,
          {
            action: "list",
          }
        );
        
        // Filtrar solo los jobs relacionados con reset de leaderboards
        // Y que NO se hayan ejecutado aún (estado ENABLED y nextRunTime en el futuro o null)
        const now = Math.floor(Date.now() / 1000); // Timestamp actual en segundos
        
        this.scheduledJobs = response.data.jobs.filter(job => {
          // Solo jobs de reset de leaderboards
          if (!job.jobId.includes("reset-leaderboards")) {
            return false;
          }
          
          // Solo jobs que están ENABLED (no pausados ni deshabilitados)
          if (job.state !== 'ENABLED') {
            return false;
          }
          
          // Solo jobs que tienen nextRunTime en el futuro o null (aún no ejecutados)
          // Si nextRunTime es null, significa que es un job recurrente que aún no tiene próxima ejecución programada
          // Si nextRunTime existe, debe ser mayor que el tiempo actual
          if (job.nextRunTime !== null && job.nextRunTime <= now) {
            return false;
          }
          
          return true;
        });
        
      } catch (error) {
        console.error("Error loading jobs:", error);
        this.showSnackbar(
          error.response?.data?.error || "Error loading scheduled jobs",
          "error"
        );
      } finally {
        this.loading.jobs = false;
      }
    },
    
    /* 
      ============================================
      MÉTODO 6: Eliminar un Job
      ============================================
    */
    async deleteJob(jobId) {
      // Usar el diálogo de confirmación personalizado
      const confirmMessage = `Are you sure you want to cancel/delete the scheduled job "${jobId}"? This action cannot be undone.`;
      this.openDialogConfirm(confirmMessage, 'deleteJobConfirmed');
      // Guardar el jobId temporalmente para usarlo en la confirmación
      this.dialogConfirm.jobId = jobId;
    },
    
    async deleteJobConfirmed() {
      console.log("[deleteJobConfirmed] Method called", this.dialogConfirm.jobId);
      const jobId = this.dialogConfirm.jobId;
      if (!jobId) {
        return;
      }
      console.log("[deleteJobConfirmed] Deleting job:", jobId);
      console.log("[deleteJobConfirmed] Requesting to delete job:", {
        action: "delete",
        jobId: jobId,
      });
      try {
        await Vue.axios.post(
          requestEndPoint.scheduleResetLeaderboards,
          {
            action: "delete",
            jobId: jobId,
          }
        );
        
        this.showSnackbar("Job cancelled successfully", "success");
        
        // Cerrar el diálogo de confirmación
        this.closeDialogConfirm();
        
        // Esperar un momento para que el servidor procese la eliminación
        // y luego recargar la lista
        setTimeout(() => {
          this.loadScheduledJobs();
        }, 500);
        
      } catch (error) {
        console.error("Error deleting job:", error);
        this.showSnackbar(
          error.response?.data?.error || "Error cancelling job",
          "error"
        );
        // Cerrar el diálogo incluso si hay error
        this.closeDialogConfirm();
      } finally {
        // Limpiar el jobId temporal
        this.dialogConfirm.jobId = null;
      }
    },
    
    /* 
      ============================================
      MÉTODO 7: Mostrar Mensaje (Snackbar)
      ============================================
      Helper function para mostrar mensajes de forma consistente
    */
    showSnackbar(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    
    /* 
      ============================================
      MÉTODO 8: Obtener Color según Estado
      ============================================
      Helper function para colorear los chips de estado
    */
    getStateColor(state) {
      const colors = {
        ENABLED: "success",
        PAUSED: "warning",
        DISABLED: "error",
        UNKNOWN: "grey",
      };
      return colors[state] || "grey";
    },
    
    /* 
      ============================================
      MÉTODO 9: Formatear Timestamp
      ============================================
      Convierte un timestamp (en segundos) a una fecha legible
    */
    formatTimestamp(timestamp) {
      if (!timestamp) return "N/A";
      // timestamp viene en segundos, convertir a milisegundos
      const date = new Date(timestamp * 1000);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    },
  },
};
</script>

<style scoped>
/* 
  ============================================
  SECCIÓN STYLE - ESTILOS CSS
  ============================================
  
  scoped: Los estilos solo aplican a este componente
  Puedes agregar estilos personalizados aquí si los necesitas
*/

/* Alinear el alert con los campos de entrada dense de Vuetify */
.schedule-info-alert {
  margin-top: 20px !important;
}
</style>
